import { useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeading } from "@/components/ui/page-heading";
import { PageContent } from "@/components/ui/page-content";
import { Stepper } from "@/components/ui/stepper";
import { SubHeading } from "./ui/sub-heading";
import { apiRequest } from "@/utils/api";
import {
  UploadStep,
  JobDetailsStep,
  AssessmentQuestionsStep,
} from "./job-spec-upload";
import { Progress } from "@radix-ui/react-progress";
import { Assessment } from "@/types";

const steps = [
  { title: "Upload job spec", description: "Upload PDF or paste text" },
  { title: "Job details", description: "Basic job information" },
  {
    title: "Assessment questions",
    description: "Review and edit assessment questions",
  },
];

export default function CreateAssessmentFlow() {
  const navigate = useNavigate();
  const { activeTeam } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [inputText, setInputText] = useState("");
  const [assessment, setAssessment] = useState<Assessment>({
    title: "",
    language: "",
    location: "",
    importance: "",
    skills: "",
    ideal_candidate: "",
    raw_text: "",
  });

  function handleInputChange(field: keyof Assessment, value: string) {
    setAssessment((prev) => ({ ...prev, [field]: value }));
  }

  function handleNext() {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  }

  function handleBack() {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep(activeStep - 1);
    }
  }

  function validateStep(step: number): boolean {
    setError("");
    switch (step) {
      case 0:
        if (!selectedFile && !inputText) {
          setError("Please upload a PDF or paste the job specification text");
          return false;
        }
        break;
      case 1:
        if (!assessment.title || !assessment.language || !assessment.location) {
          setError("Please fill in all required fields");
          return false;
        }
        break;
      case 2:
        if (!assessment.ideal_candidate) {
          setError("Please fill in all required fields");
          return false;
        }
        break;
    }
    return true;
  }

  const handleSubmit = async () => {
    if (!validateStep(3) || !activeTeam) return;

    setIsLoading(true);

    assessment.raw_text = inputText || "";

    try {
      const data = await apiRequest<{ id: string }>("/assessments", {
        method: "POST",
        body: {
          ...assessment,
          team_id: activeTeam.id,
        },
      });
      navigate(`/assessments/${data.id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageHeading className="!mb-2">
        Create new assessment
        <SubHeading>
          Fill in the details below to create a new assessment
        </SubHeading>
      </PageHeading>

      <PageContent>
        <Card className="mb-4">
          <CardContent className="pt-6">
            <Stepper steps={steps} activeStep={activeStep} className="mb-6" />
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardContent className="pt-6 space-y-6">
            {activeStep === 0 && (
              <UploadStep
                isLoading={isLoading}
                error={error}
                onFileSelect={setSelectedFile}
                onTextInput={setInputText}
                inputText={inputText}
              />
            )}

            {activeStep === 1 && (
              <JobDetailsStep
                assessment={assessment}
                onInputChange={handleInputChange}
                error={error}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                selectedFile={selectedFile}
                inputText={inputText}
                setInputText={setInputText}
                setError={setError}
              />
            )}

            {activeStep === 2 && (
              <AssessmentQuestionsStep
                assessment={assessment}
                questions={assessment.questions || []}
                onQuestionsGenerated={(questions) =>
                  setAssessment((prev) => ({ ...prev, questions }))
                }
                onQuestionChange={(id, field, value) =>
                  setAssessment((prev) => ({
                    ...prev,
                    questions: prev.questions?.map((q) =>
                      q.id === id ? { ...q, [field]: value } : q
                    ),
                  }))
                }
                error={error}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                isLoading={isLoading}
              />
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between items-center">
          <Button
            onClick={handleBack}
            variant="outline"
            disabled={isLoading || isGenerating}
          >
            {activeStep === 0 ? "Cancel" : "Previous"}
          </Button>
          <Button
            onClick={handleNext}
            isLoading={isLoading}
            disabled={isGenerating}
          >
            {activeStep === steps.length - 1 ? "Create assessment" : "Next"}
          </Button>
        </div>

        {isLoading && <Progress />}
      </PageContent>
    </>
  );
}
